<template>
  <div class="container-fluid">
    <div class="row">
      <b-modal size="md" hide-footer ref="FinancesCompensationsModal" centered :modal-class="isModalLoading ? 'loading' : ''">
        <template v-slot:modal-header="{ cancel }">
          <h5 class="modal-title">
            {{ !!editedItem.id ? "Edit compensation" : "Create compensation" }}
          </h5>
          <button type="button" aria-label="Close" class="close" @click.prevent="cancel">
            <span class="material-icons">
              close
            </span>
          </button>
        </template>
        <FinancesCompensationsForm v-if="dialog" :dataProps="editedItem" :allUsers="getAllUsers"
          :currencies="currencies" :taxTypes="taxTypes" :edit="!!editedItem.id" :modal="true" :isLoading="isModalLoading"
          :show-preloader="true" @submit="submit" />
      </b-modal>

      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">

          <PageHeader showBreadcrumbs page-title="Compensations">
            <template #buttons>
              <b-button class="button title-container__controls-item" :variant="isShowFilter ? 'info' : 'secondary'"
                @click="toggleShowFilter">
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button v-if="$can('create-finances_compensations')" variant="success" class="button"
                @click="editEntityModalOpen()" :disabled="loading">
                Create
              </b-button>
            </template>
          </PageHeader>
          <Filters ref="filtersComponent" v-show="isShowFilter" textButtonSubmit="Filter" :data="getTableFilters" :isLoading="loading && isInitialDataLoaded"
            :data-changes="true" resetToInitial @changes="checkChanges" @submit="setFilterData" />

          <div v-if="loading && !isInitialDataLoaded" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <div class="full-height-content">
            <TableForPage v-if="getUsersData.length" :tableData="getUsersData" :fields="table.fields" :slots="table.slots" :actions="table.actions"
              :isDataLoading="loading && isInitialDataLoaded"
              :sticky-header="false" @edit="editEntityModalOpen" @remove="deleteEntity">
              <template slot="index" slot-scope="data">
                {{ data.index + 1 }}
              </template>
              <template slot="user" slot-scope="data">
                <TableUserItem :user="data.item.user" />
              </template>
              <template v-slot:comment="data">
                <div class="user-comment__list p-0">
                  <TextEditor :show-text="true" data-already-loaded :entity-type="commentEntityTypes.UserCompensations"
                    :entity-id="data.item.user.id" />
                </div>
              </template>
              <template slot="compensation" slot-scope="data">
                <multiselect v-model="form.compensations[data.item.user.id]" :options="data.item.compensations"
                  track-by="id" label="label" :allow-empty="false" :showLabels="false" />
              </template>
            </TableForPage>
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getUsersData.length && !filterState"
              title="No compensations yet"
              description="You haven't created any compensations yet. Add one to track payments, bonuses, or benefits."
              customIcon="schedule"
              customIconClasses="material-symbols-outlined empty-state__icon"
              :withoutButton="true"
            />
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getUsersData.length && filterState"
              @reset-filters="handleResetFilters"
            />
          </div>
          <error-modal ref="errorModal" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { commentEntityTypes } from '@mocks/comment-entity-types'
import Moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import FinancesCompensationsForm from '../components/forms/FinancesCompensationsForm'
import FinancesMixin from '../mixins/finances'
import FrontFilterMixin from '../mixins/FrontFilterMixin'
import ErrorModal from '../components/modals/ErrorModal.vue'
import TableForPage from '@tables/TableForPage'
import { getDateFrom } from '../functions/format_date'
import Filters from '@components/Filter/Filter'
import TableUserItem from '@components/tables/TableUserItem'
import TextEditor from '@components/TextEditor'
import { permissions } from '@mocks/permissions'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'

export default {
  name: 'FinancesCompensations',
  components: {
    ErrorModal,
    FinancesCompensationsForm,
    TableForPage,
    TableUserItem,
    Filters,
    TextEditor,
    PageHeader,
    Preloader,
    EmptyStateNoResults
  },
  mixins: [FinancesMixin, FrontFilterMixin],
  data () {
    const commentField = this.$can(permissions.ReadAllComments) ? [
      {
        key: 'comment',
        label: 'Comment',
        class: 'column-long-name'
      }
    ] : []
    return {
      loading: true,
      isModalLoading: false,
      dialog: false,
      defaultFormData: {},
      isAllOptionsFilterSet: false,
      isCompensationsDataLoaded: false,
      form: {
        compensations: []
      },
      editedItem: {},
      filteredUsers: [],
      table: {
        fields: [
          {
            key: 'index',
            label: '#',
            sortable: false,
            class: 'number-column'
          },
          {
            key: 'user',
            label: 'User',
            sortable: true,
            class: 'column-long-name'
          },
          {
            key: 'compensation',
            label: 'Compensation',
            class: 'column-compensations'
          },
          ...commentField,
          {
            key: 'actions',
            label: 'Actions',
            class: 'column-action-compensation'
          }
        ],
        slots: {
          idex: {
            name: 'index'
          },
          user: {
            name: 'user'
          },
          comment: {
            name: 'comment'
          },
          compensation: {
            name: 'compensation'
          }
        },
        data: [],
        totalsData: [],
        actions: [
          { label: 'Edit', event: 'edit', show: this.$can('update_own-finances_compensations') },
          { label: 'Delete', event: 'remove', show: this.$can('delete_own-finances_compensations') }
        ]
      },
      filter: {
        fields: {
          'users': {
            type: 'multiselect',
            name: 'users',
            label: 'Select user',
            class: 'filter-inputs',
            valueField: 'jira_user_name',
            selectLabel: 'label',
            options: [],
            value: []
          },
          'user_status': {
            type: 'select',
            name: 'user_status',
            label: 'Select status',
            class: 'filter-inputs',
            valueField: 'status',
            selectLabel: 'label',
            options: [{ status: 'all', label: 'All' }, { status: 'active', label: 'Active' }, { status: 'inactive', label: 'Inactive' }],
            value: null
          },
          'users_roles': {
            type: 'multiselect',
            name: 'users_roles',
            label: 'Select role',
            class: 'filter-inputs',
            valueField: 'name',
            selectLabel: 'display_name',
            options: [],
            value: []
          },
          'employment_type': {
            type: 'select',
            name: 'employment_type',
            label: 'Select employment type',
            class: 'filter-inputs',
            valueField: 'type',
            selectLabel: 'label',
            options: [
              { type: 'all', label: 'All employment types' },
              { type: 'team_member', label: 'Team member' },
              { type: 'independent', label: 'Independent' },
              { type: 'partner', label: 'Partner' }
            ],
            value: { type: 'all', label: 'All employment types' },
            defaultValue: { type: 'all', label: 'All employment types' }
          },
          'partners': {
            type: 'multiselect',
            name: 'partners',
            label: 'Select partner',
            class: 'col-md-3',
            valueField: 'id',
            selectLabel: 'company_name',
            options: [],
            value: [],
            hidden: true
          }
        }
      }
    }
  },
  created () {
    (async () => {
      this.loading = true
      await this.getEntities()
      if (this.$can(permissions.ReadAllComments)) {
        await this.fetchComments({ type: commentEntityTypes.UserCompensations })
      }
      Promise.all([
        this.$api.users.getAllUsers(),
        this.$api.partners.getAllPartners(),
        this.$api.finances.getCurrencies(),
        this.$api.finances.getTaxTypes(),
        this.fetchAllProjects(),
        this.$api.projects.getJiraProjects()
      ])
        .then(response => {
          this.setAllOptionsFilter()
        })
        .catch(e => {})
        .finally(() => {
          this.loading = false
        })
    })()
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isCompensationsDataLoaded
    },
    ...mapGetters({
      dateFormat: 'dateFormat',
      getProjects: 'projects/getProjects',
      taxTypes: 'finances/getTaxTypes',
      getAllUsers: 'getAllUsersWithTrashed',
      getUserRoles: 'getRoles',
      getAllPartners: 'partners/getAllPartners'
    }),
    isLoading () {
      return this.loading
    },
    getTableFilters () {
      return this.filter.fields || {}
    },
    currencies () {
      return this.$store.getters.getCurrencies.filter(currency => ['USD', 'UAH'].includes(currency.code)) || []
    },
    getUsersData () {
      if (!this.filteredUsers.length && !this.filterState) {
        return this.table.data
      }
      return this.filteredUsers || []
    },
    commentEntityTypes () {
      return commentEntityTypes
    }
  },
  watch: {
    'filter.date_from': function (val) {
      if (
        Moment(val, 'YYYY-MM-DD').isAfter(this.filter.date_to, 'YYYY-MM-DD')
      ) {
        this.filter.date_to = this.filter.date_from
      }
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments'
    }),
    async fetchAllProjects () {
      if (this.getProjects.length) return

      await this.$api.projects.getProjects()
    },
    setAllOptionsFilter () {
      this.filter.fields.users.options = this.getAllUsers
      this.filter.fields.users_roles.options = [
        {
          display_name: 'All',
          name: 'all'
        },
        ...this.getUserRoles
      ]
      this.filter.fields.partners.options = [
        {
          company_name: 'All partners',
          name: 'All'
        },
        ...this.getAllPartners // for example
      ]

      this.isAllOptionsFilterSet = true
    },
    getDate (date, target) {
      let result = getDateFrom(date, target)
      if (target === 'from') {
        this.filter.date_from = result
      } else {
        this.filter.date_to = result
      }
    },
    getEntities () {
      this.loading = true
      this.axios
        .get('/api/finances/compensations', {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(response => {
          let compensationsData = response.data
          this.table.data = compensationsData
          this.setFormCompensations()
          // let formCompensations = [];
          // compensationsData.forEach(function (item, index) {
          //     formCompensations[item.user.id] = item.compensations.find(
          //         function(compensation) {
          //             return !!compensation.is_actual_value;
          //         }
          //     );
          //     formCompensations[item.user.id].user = item.user;
          // });
          //
          // this.form.compensations = formCompensations;
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.loading = false
          this.isCompensationsDataLoaded = true
        })
    },
    setFormCompensations () {
      let formCompensations = []
      let compensationsData = this.table.data
      compensationsData.forEach(function (item, index) {
        formCompensations[item.user.id] = item.compensations.find(function (
          compensation
        ) {
          return !!compensation.is_actual_value
        })

        if (formCompensations[item.user.id] === undefined) {
          formCompensations[item.user.id] = item.compensations[0]
        }
        formCompensations[item.user.id].user = item.user
      })

      this.form.compensations = formCompensations
    },
    create (data, files) {
      this.isModalLoading = true

      this.axios
        .post('/api/finances/compensations', data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Compensation successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.dialog = false
          this.getEntities()
          this.editedItem = {}
          this.$refs['FinancesCompensationsModal'].hide()
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    editEntityModalOpen (item) {
      let data = {}
      let additionalData = {}

      if (item) {
        data = this.form.compensations[item.user.id]
        additionalData = { user: item.user }
      }

      this.editedItem = Object.assign(Object.assign({}, data), additionalData)

      this.dialog = true
      this.$refs['FinancesCompensationsModal'].show()
    },
    edit (data) {
      this.isModalLoading = true
      data['_method'] = 'PUT'
      this.axios
        .post('/api/finances/compensations/' + this.editedItem.id, data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Compensation successfully saved',
            'Success',
            this.$store.state.snotify
          )

          this.dialog = false
          this.getEntities()
          this.editedItem = {}
          this.$refs['FinancesCompensationsModal'].hide()
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    submit (data, files) {
      if (this.editedItem.id) {
        this.edit(data, files)
      } else {
        this.create(data, files)
      }
    },
    async deleteEntity (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the compensation?'
      )

      if (!confirm) return

      const id = this.form.compensations[item.user.id].id

      this.loading = true
      this.axios
        .delete('/api/finances/compensations/' + id, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .finally(() => {
          this.getEntities()
        })
    },
    setFilterData (data) {
      const filterByUser = data.users ? data.users.value : []
      const filterByUserStatus = data.user_status ? data.user_status.value : null
      const filterByUserRole = data.users_roles ? data.users_roles.value : []
      const filterByEmplType = data.employment_type ? data.employment_type.value : null
      const filterByPartners = data.partners ? data.partners.value : []

      let users = [...this.table.data]

      this.checkFilters(data)

      if (filterByUser.length) {
        users = this.usersFilter(users, filterByUser)
      }

      if (filterByUserStatus) {
        users = this.statusFilter(users, filterByUserStatus)
      }

      if (filterByUserRole.length) {
        users = this.rolesFilter(users, filterByUserRole)
      }

      if (filterByEmplType) {
        users = this.emplTypeFilter(users, filterByEmplType)
      }

      if (filterByPartners.length) {
        users = this.partnersFilter(users, filterByPartners)
      }

      this.filteredUsers = users
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    }
  },
  updated () {}
}
</script>

<style scoped>
.table-wrapper {
  padding-top: 20px;
}
.table-wrapper h3 {
  margin-bottom: 30px;
}
.entities-table {
  background-color: #fff;
}
/*.entities-table-wrapper {*/
/*  max-height: 600px;*/
/*  overflow-y: auto;*/
/*}*/
.spinner-border {
  border-width: 0.25rem !important;
}
.filter-buttons {
  padding-top: 34px;
}
</style>
